import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  AlertTriangle,
  MoreHorizontal,
  LogIn,
  UserPlus,
} from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAllUsers,
  useAllAccounts,
  useOrphanedUsers,
  useAdminResendInvite,
  useAdminForceLink,
  useAdminResetPassword,
  useAdminSuspendUser,
  useAdminDeleteUser,
  useAdminUpdateUserRole,
  useAdminInviteUser,
  type AdminUser,
  type OrphanedAuthUser,
} from "@/hooks/use-admin";
import { formatRole, formatRelativeDate } from "@/lib/format-utils";

// ─── Edit Role Sheet ──────────────────────────────────────────────────────────

interface EditRoleSheetProps {
  open: boolean;
  onClose: () => void;
  user: AdminUser;
}

function EditRoleSheet({ open, onClose, user }: EditRoleSheetProps) {
  const [role, setRole] = useState<string>(user.account_role);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateRole = useAdminUpdateUserRole();

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await updateRole.mutateAsync({
        userId: user.id,
        account_role: role,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[400px] sm:max-w-[400px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Edit Role</SheetTitle>
        </SheetHeader>
        <form
          id="edit-role-form"
          className="flex-1 py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <p className="typo-body text-[color:var(--color-text-md)]">
            {user.email}
          </p>
          <SettingsField label="Account Role">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </SettingsField>
          {error && <p className="typo-body text-destructive">{error}</p>}
        </form>
        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Save"
          isSubmitting={submitting}
          formId="edit-role-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Create User Sheet ────────────────────────────────────────────────────────

interface CreateUserSheetProps {
  open: boolean;
  onClose: () => void;
}

function CreateUserSheet({ open, onClose }: CreateUserSheetProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [role, setRole] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: accountsData } = useAllAccounts();
  const inviteUser = useAdminInviteUser();

  function handleClose() {
    setEmail("");
    setFirstName("");
    setLastName("");
    setAccountId("");
    setRole("admin");
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!accountId) {
      setError("Select an account");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await inviteUser.mutateAsync({
        accountId,
        data: {
          email,
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          account_role: role,
        },
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  }

  const accounts = accountsData?.accounts ?? [];

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Create User</SheetTitle>
        </SheetHeader>
        <form
          id="create-user-form"
          className="flex-1 overflow-y-auto py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <SettingsField label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </SettingsField>
          <div className="grid grid-cols-2 gap-3">
            <SettingsField label="First Name">
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
              />
            </SettingsField>
            <SettingsField label="Last Name">
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </SettingsField>
          </div>
          <SettingsField label="Account">
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select account…" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SettingsField>
          <SettingsField label="Role">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </SettingsField>
          {error && <p className="typo-body text-destructive">{error}</p>}
        </form>
        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Create User"
          isSubmitting={submitting || !email.trim() || !accountId}
          formId="create-user-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Row Action Menu ──────────────────────────────────────────────────────────

interface PendingConfirm {
  title: string;
  description: string;
  onConfirm: () => void;
}

function UserRowActions({ user }: { user: AdminUser }) {
  const navigate = useNavigate();
  const resend = useAdminResendInvite();
  const forceLink = useAdminForceLink();
  const resetPassword = useAdminResetPassword();
  const suspend = useAdminSuspendUser();
  const deleteUser = useAdminDeleteUser();
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const canResend = user.status === "invited";

  async function handleAction(
    action: "resend" | "force-link" | "reset-password",
  ) {
    try {
      if (action === "resend") await resend.mutateAsync(user.id);
      else if (action === "force-link") await forceLink.mutateAsync(user.id);
      else if (action === "reset-password")
        await resetPassword.mutateAsync(user.id);
    } catch {
      // Errors surface via toast in a future pass
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label="User actions"
            className="h-7 w-7 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-52 bg-[color:var(--color-surface)] shadow-md border-[color:var(--color-border)]"
        >
          <DropdownMenuItem
            onClick={() =>
              navigate(`/settings/adva/accounts/${user.account_id}/users`)
            }
          >
            <LogIn className="mr-2 h-3.5 w-3.5" />
            Impersonate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={!canResend}
            onClick={() => handleAction("resend")}
          >
            Resend invite
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAction("reset-password")}>
            Reset password
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAction("force-link")}>
            Force-link to auth user
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEditRoleOpen(true)}>
            Edit role
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() =>
              setPending({
                title: "Suspend user?",
                description: `Suspend ${user.email}? They will lose access to this account.`,
                onConfirm: () => suspend.mutate(user.id),
              })
            }
          >
            Suspend
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() =>
              setPending({
                title: "Delete user?",
                description: `Permanently delete ${user.email}? This cannot be undone.`,
                onConfirm: () => deleteUser.mutate(user.id),
              })
            }
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editRoleOpen && (
        <EditRoleSheet
          open={editRoleOpen}
          onClose={() => setEditRoleOpen(false)}
          user={user}
        />
      )}

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.title ?? ""}
        description={pending?.description ?? ""}
        confirmLabel="Confirm"
        onConfirm={() => {
          pending?.onConfirm();
          setPending(null);
        }}
      />
    </>
  );
}

// ─── Account Users Tab ────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function AccountUsersTab() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [pageIndex, setPageIndex] = useState(0);

  const { data: accountsData } = useAllAccounts();
  const { data, isLoading } = useAllUsers({
    q: search || undefined,
    status: status !== "all" ? status : undefined,
    role: role !== "all" ? role : undefined,
    account_id: accountFilter !== "all" ? accountFilter : undefined,
    limit: PAGE_SIZE,
    offset: pageIndex * PAGE_SIZE,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function resetPage() {
    setPageIndex(0);
  }

  const accounts = accountsData?.accounts ?? [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-text-md)]" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            placeholder="Search email or name…"
            className="pl-9"
          />
        </div>
        <Select
          value={accountFilter}
          onValueChange={(v) => {
            setAccountFilter(v);
            resetPage();
          }}
        >
          <SelectTrigger className="w-auto min-w-[160px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            resetPage();
          }}
        >
          <SelectTrigger className="w-auto min-w-[140px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={role}
          onValueChange={(v) => {
            setRole(v);
            resetPage();
          }}
        >
          <SelectTrigger className="w-auto min-w-[130px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="billing">Billing</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SettingsTable
        colSpan={6}
        isLoading={isLoading}
        isEmpty={users.length === 0}
        emptyMessage="No users found."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Account
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Role
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Last Sign-in
            </TableHead>
            <TableHead className="w-12 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const name = [user.first_name, user.last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <TableRow
                key={user.id}
                className="h-9 border-b border-[color:var(--color-border)] last:border-0"
              >
                <TableCell className="px-4 py-1.5">
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    {name || user.email}
                  </p>
                  {name && (
                    <p className="typo-body text-[color:var(--color-text-md)]">
                      {user.email}
                    </p>
                  )}
                </TableCell>
                <TableCell className="px-4 py-1.5 typo-body text-[color:var(--color-text-md)]">
                  {user.account_name}
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <Badge variant="secondary">
                    {formatRole(user.account_role)}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <Badge
                    variant={user.status === "active" ? "default" : "outline"}
                  >
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-[color:var(--color-text-md)]">
                  {formatRelativeDate(user.last_sign_in_at)}
                </TableCell>
                <TableCell className="px-4 py-1.5 text-right">
                  <UserRowActions user={user} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </SettingsTable>

      {total > 0 && (
        <div className="flex items-center justify-between px-1 py-4">
          <p className="typo-body text-[color:var(--color-text-md)]">
            Showing {pageIndex * PAGE_SIZE + 1}–
            {Math.min((pageIndex + 1) * PAGE_SIZE, total)} of{" "}
            {total.toLocaleString()}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex <= 0}
                onClick={() => setPageIndex((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="typo-body text-[color:var(--color-text-md)]">
                {pageIndex + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex >= totalPages - 1}
                onClick={() => setPageIndex((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Orphaned Users Tab ───────────────────────────────────────────────────────

function OrphanedUsersTab() {
  const { data, isLoading } = useOrphanedUsers();
  const users = (data?.users ?? []) as OrphanedAuthUser[];

  return (
    <div>
      <div className="mb-4 flex items-start gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--color-text-md)]" />
        <div className="typo-body text-[color:var(--color-text-md)]">
          These auth users have no{" "}
          <code className="typo-mono">account_users</code> row — they land on{" "}
          <code className="typo-mono">/no-account</code> at login. Invite them
          to their destination account to recover.
        </div>
      </div>

      <SettingsTable
        colSpan={4}
        isLoading={isLoading}
        isEmpty={users.length === 0}
        emptyMessage="No orphaned auth users found."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Email
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Email Confirmed
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Auth Created
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Last Sign-in
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow
              key={user.auth_user_id}
              className="h-9 border-b border-[color:var(--color-border)] last:border-0"
            >
              <TableCell className="px-4 py-1.5 typo-body text-[color:var(--color-text-hi)]">
                {user.email}
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <Badge variant={user.email_confirmed ? "default" : "outline"}>
                  {user.email_confirmed ? "Confirmed" : "Unconfirmed"}
                </Badge>
              </TableCell>
              <TableCell className="px-4 py-1.5 typo-body text-[color:var(--color-text-md)]">
                {new Date(user.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="px-4 py-1.5 typo-body text-[color:var(--color-text-md)]">
                {formatRelativeDate(user.last_sign_in_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function V2AdvaUsersPage() {
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <SettingsPage
      breadcrumbs={[{ label: "Adva" }, { label: "Users" }]}
      title="Users"
      subtitle="All users across every account."
      actions={
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Create User
        </Button>
      }
    >
      <Tabs defaultValue="account-users">
        <TabsList className="mb-4">
          <TabsTrigger value="account-users">Account Users</TabsTrigger>
          <TabsTrigger value="orphaned">Orphaned Auth Users</TabsTrigger>
        </TabsList>
        <TabsContent value="account-users">
          <AccountUsersTab />
        </TabsContent>
        <TabsContent value="orphaned">
          <OrphanedUsersTab />
        </TabsContent>
      </Tabs>

      <CreateUserSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </SettingsPage>
  );
}
